import { useState, useEffect } from 'react'
import { ShoppingList } from '../types'

interface ShoppingListFormProps {
  isOpen: boolean
  onClose: () => void
  onSave: (list: Omit<ShoppingList, 'createdAt' | 'updatedAt'>) => Promise<void>
  list?: ShoppingList
  isEditing?: boolean
}

const ShoppingListForm = ({ isOpen, onClose, onSave, list, isEditing = false }: ShoppingListFormProps) => {
  const [formData, setFormData] = useState({
    name: list?.name || '',
    items: list?.items || []
  })
  
  const [isLoading, setIsLoading] = useState(false)

  // Update form data when list prop changes
  useEffect(() => {
    if (list) {
      setFormData({
        name: list.name,
        items: list.items
      })
    } else {
      // Reset form for new list
      setFormData({
        name: '',
        items: []
      })
    }
  }, [list])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name.trim()) {
      alert('Listan nimi on pakollinen')
      return
    }

    setIsLoading(true)
    try {
      await onSave({
        name: formData.name.trim(),
        items: formData.items
      })
      onClose()
    } catch (error) {
      console.error('Form save error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {isEditing ? 'Muokkaa ostoslistaa' : 'Luo uusi ostoslista'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Listan nimi *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Esim. Viikonloppu"
                required
              />
            </div>

            <div className="text-sm text-gray-600">
              {formData.items.length > 0 ? (
                <p>Lista sisältää {formData.items.length} tuotetta. Voit lisätä tuotteita listan luomisen jälkeen.</p>
              ) : (
                <p>Luo lista ensin, sitten voit lisätä tuotteita tuotesivulta.</p>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 border border-gray-300 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
              >
                Peruuta
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {isLoading ? 'Tallennetaan...' : (isEditing ? 'Tallenna' : 'Luo lista')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ShoppingListForm 